import { api } from './api'

export interface CompanyBankAccount {
  id: string
  bankName: string
  accountHolderName: string
  accountNumber: string
  accountNumberLast: string
  isPrimary: boolean
  isActive: boolean
  displayPriority: number
  minimumAmount?: number
  description?: string
  createdAt: string
  updatedAt: string
}

export interface CreateBankAccountData {
  bankName: string
  accountHolderName: string
  accountNumber: string
  bankCode?: string
  branchCode?: string
  accountType?: 'CURRENT' | 'SAVINGS'
  currency?: string
  isActive?: boolean
  isPrimary?: boolean
  description?: string
  displayPriority?: number
  minimumAmount?: number
  notes?: string
}

export interface UpdateBankAccountData {
  bankName?: string
  accountHolderName?: string
  accountNumber?: string
  bankCode?: string
  branchCode?: string
  accountType?: 'CURRENT' | 'SAVINGS'
  currency?: string
  isActive?: boolean
  isPrimary?: boolean
  description?: string
  displayPriority?: number
  minimumAmount?: number
  notes?: string
}

export interface ApiResponse<T> {
  data: T
  message: string
  success: boolean
}

class BankAccountService {
  async getAllAccounts(): Promise<CompanyBankAccount[]> {
    const response = await api.get<CompanyBankAccount[]>('/payments/company-bank-accounts')
    return response.data
  }

  async getAccountById(id: string): Promise<CompanyBankAccount> {
    const response = await api.get<ApiResponse<CompanyBankAccount>>(`/payments/company-bank-accounts/${id}`)
    return response.data.data
  }

  async createAccount(data: CreateBankAccountData): Promise<CompanyBankAccount> {
    const response = await api.post<CompanyBankAccount>('/payments/company-bank-accounts', data)
    return response.data
  }

  async updateAccount(id: string, data: UpdateBankAccountData): Promise<CompanyBankAccount> {
    const response = await api.patch<CompanyBankAccount>(`/payments/company-bank-accounts/${id}`, data)
    return response.data
  }

  async deleteAccount(id: string): Promise<void> {
    await api.delete(`/payments/company-bank-accounts/${id}`)
  }

  async setPrimaryAccount(id: string): Promise<CompanyBankAccount> {
    const response = await api.patch<ApiResponse<CompanyBankAccount>>(`/payments/company-bank-accounts/${id}/primary`)
    return response.data.data
  }

  async toggleAccountStatus(id: string): Promise<CompanyBankAccount> {
    const response = await api.patch<ApiResponse<CompanyBankAccount>>(`/payments/company-bank-accounts/${id}/toggle-status`)
    return response.data.data
  }

  async getStatistics(): Promise<{
    total: number
    active: number
    inactive: number
    primary: number
  }> {
    const response = await api.get<ApiResponse<{
      total: number
      active: number
      inactive: number
      primary: number
    }>>('/payments/company-bank-accounts/statistics')
    return response.data.data
  }
}

export const bankAccountService = new BankAccountService()